import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  companyInfo,
  aboutContent,
  whyChooseData,
  groupCompanies,
  certificationData,
  projectStats,
} from "../data/index";

const About = () => {
  const stats = projectStats || [];
  const companies = groupCompanies || [];

  return (
    <>
      <Helmet>
        <title>About Us | Al Abeer Diamond</title>
        <meta
          name="description"
          content={`Learn about Al Abeer Diamond - our mission, vision, values, and commitment to excellence in construction and engineering services in Saudi Arabia.`}
        />
        <meta
          name="keywords"
          content="about al abeer diamond, construction company saudi arabia, building excellence, construction services"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden bg-secondary">
        <div className="absolute inset-0 bg-hero-gradient opacity-90"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-primary/20 text-primary-light rounded-full text-sm font-medium font-oswald tracking-wider mb-4">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-oswald leading-tight">
              Building Excellence, <br />
              <span className="text-primary-light">Ensuring Safety</span>
            </h1>
            <p className="text-gray-300 text-lg mt-4 max-w-2xl">
              {companyInfo.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="section-badge">Who We Are</span>
              <h2 className="heading-primary mt-4">
                Welcome to{" "}
                <span className="text-gradient">Al Abeer Diamond</span>
              </h2>
              <p className="text-gray-600 leading-relaxed mt-4">
                {aboutContent.para}
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                {aboutContent.para2}
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                {aboutContent.para3}
              </p>

              <div className="grid grid-cols-2 gap-4 mt-8">
                {stats.slice(0, 4).map((stat) => (
                  <div
                    key={stat.id}
                    className="bg-white rounded-xl p-4 text-center shadow-soft"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-primary font-oswald">
                      {stat.number}
                    </div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-hover">
                <img
                  src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80"
                  alt="About Al Abeer Diamond"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/10 rounded-full -z-10"></div>
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-primary/20 rounded-full -z-10"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="section-badge">Our Purpose</span>
              <h2 className="heading-primary mt-4">
                Mission & <span className="text-gradient">Vision</span>
              </h2>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-8 shadow-soft card-hover"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <i className="uil uil-bullseye text-2xl text-primary"></i>
              </div>
              <h3 className="text-2xl font-bold text-secondary font-oswald mb-3">
                Our Mission
              </h3>
              <p className="text-gray-600 leading-relaxed">
                To provide integrated, reliable, and efficient services across
                construction, technical works, manpower, and MEP solutions,
                while upholding the highest standards of quality, safety, and
                client satisfaction.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-8 shadow-soft card-hover"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <i className="uil uil-eye text-2xl text-primary"></i>
              </div>
              <h3 className="text-2xl font-bold text-secondary font-oswald mb-3">
                Our Vision
              </h3>
              <p className="text-gray-600 leading-relaxed">
                To be a leading and trusted multi-disciplinary company in Saudi
                Arabia, delivering innovative, high-quality, and sustainable
                solutions that contribute to the nation's growth and Vision
                2030.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="section-badge">What Drives Us</span>
              <h2 className="heading-primary mt-4">
                Our Core <span className="text-gradient">Values</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto mt-4">
                The principles that guide everything we do
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseData.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 text-center shadow-soft card-hover border border-gray-100"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i
                    className={`${item.icon || "uil uil-star"} text-2xl text-primary`}
                  ></i>
                </div>
                <h3 className="text-xl font-bold text-secondary font-oswald mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Group Companies - Enhanced Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="section-badge">Our Network</span>
            <h2 className="heading-primary mt-4">
              Group of <span className="text-gradient">Companies</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">
              We operate as part of a multinational group with a strong global
              presence across multiple countries
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {companies.map((company, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-6 text-center shadow-soft card-hover border border-gray-100 group flex flex-col items-center"
              >
                {/* Logo container */}
                <div className="w-24 h-24 bg-white rounded-xl flex items-center justify-center mb-4 p-3 border border-gray-100 group-hover:border-primary/30 transition-all duration-300">
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt={company.name}
                      loading="lazy"
                      className="max-w-full max-h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <span
                    className="hidden w-full h-full items-center justify-center"
                    aria-hidden="true"
                  >
                    <i className="uil uil-building text-2xl text-primary"></i>
                  </span>
                </div>

                <p className="text-sm text-gray-700 font-medium leading-tight">
                  {company.name}
                </p>
                <div className="mt-3 w-12 h-0.5 bg-primary/30 mx-auto group-hover:w-20 transition-all duration-300"></div>
              </motion.div>
            ))}
          </div>

          {/* Global Presence Map */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl p-8 text-center"
          >
            <div className="flex flex-wrap justify-center gap-6">
              {[
                "UAE",
                "Pakistan",
                "Indonesia",
                "Bahrain",
                "Saudi Arabia",
                "Myanmar",
                "Thailand",
              ].map((country) => (
                <div key={country} className="flex items-center space-x-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-sm text-gray-600">{country}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-4">
              <i className="uil uil-map-marker text-primary mr-1"></i>
              Operating across 7 countries worldwide
            </p>
          </motion.div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="section-badge">Quality Assured</span>
            <h2 className="heading-primary mt-4">
              Certifications &{" "}
              <span className="text-gradient">Accreditations</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certificationData.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 text-center shadow-soft card-hover border border-gray-100"
              >
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${cert.icon} text-3xl text-primary`}></i>
                </div>
                <h4 className="text-sm font-semibold text-secondary">
                  {cert.title}
                </h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white font-oswald">
              Ready to Start Your Project?
            </h2>
            <p className="text-white/80 text-lg mt-4 max-w-2xl mx-auto">
              Contact us today to discuss how we can bring your vision to life
            </p>
            <Link
              to="/contact"
              className="inline-block mt-6 bg-white text-primary px-8 py-3 rounded-full font-medium hover:shadow-lg transition-all hover:-translate-y-1"
            >
              Get in Touch <i className="uil uil-arrow-right ml-2"></i>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;
